import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Share2, Copy, Trash2, Plus, Link as LinkIcon, Radio, RadioTower,
} from "lucide-react";

const publicUrlFor = (token) => `${window.location.origin}/tp/${token}`;

const isExpired = (l) => {
  try { return new Date(l.expires_at) < new Date(); } catch { return false; }
};

/**
 * Share Hub — launched from inside the main Teleprompter.
 * Lists approved retrancas of the current episode and lets the host:
 *   - generate / copy / revoke a public link per retranca
 *   - activate "live sync" mode for a given link (becomes the controller)
 *
 * Props:
 *   - open, onOpenChange: controlled dialog state
 *   - episode: full episode (we read id, retrancas)
 *   - activeSyncToken: currently synced token (if any)
 *   - onSelectSyncToken(token|null): switches live sync token
 */
export default function TPShareHub({
  open, onOpenChange, episode, activeSyncToken, onSelectSyncToken,
}) {
  const [linksByRetranca, setLinksByRetranca] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const approved = useMemo(
    () => (episode?.retrancas || []).filter((r) => r.approved),
    [episode]
  );

  const loadAll = useCallback(async () => {
    if (!episode?.id || approved.length === 0) {
      setLinksByRetranca({});
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(
        approved.map((r) =>
          api.get(`/episodes/${episode.id}/retrancas/${r.id}/share-links`)
            .then((res) => [r.id, res.data])
            .catch(() => [r.id, []])
        )
      );
      const map = {};
      for (const [rid, data] of results) map[rid] = data || [];
      setLinksByRetranca(map);
    } finally {
      setLoading(false);
    }
  }, [episode?.id, approved]);

  useEffect(() => {
    if (open) loadAll();
  }, [open, loadAll]);

  const createLink = async (retrancaId) => {
    setBusyId(retrancaId);
    try {
      const r = await api.post(
        `/episodes/${episode.id}/retrancas/${retrancaId}/share-links`,
        { expires_in_days: 7 }
      );
      setLinksByRetranca((prev) => ({
        ...prev,
        [retrancaId]: [r.data, ...(prev[retrancaId] || [])],
      }));
      await copy(publicUrlFor(r.data.token));
      toast.success("Link criado e copiado!");
      // Auto-engage live sync for the fresh link
      if (onSelectSyncToken) onSelectSyncToken(r.data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar link.");
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); }
    catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  const revoke = async (link) => {
    if (!window.confirm("Revogar este link? Quem já tiver recebido não conseguirá mais acessar.")) return;
    try {
      await api.post(`/share-links/${link.id}/revoke`);
      toast.message("Link revogado.");
      if (activeSyncToken === link.token && onSelectSyncToken) onSelectSyncToken(null);
      loadAll();
    } catch { toast.error("Falha ao revogar."); }
  };

  const toggleSync = (token) => {
    if (!onSelectSyncToken) return;
    onSelectSyncToken(activeSyncToken === token ? null : token);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl bg-zinc-950 border-white/10 text-zinc-100" data-testid="tp-share-hub">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2 text-white">
            <Share2 className="w-5 h-5 text-amber-400" />
            Compartilhar teleprompter
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Gere um link público (sem login) para cada lauda. Ao ativar o <span className="text-amber-300 font-semibold">controle ao vivo</span>, play/pause, velocidade e tamanho da fonte são sincronizados entre este teleprompter e quem abrir o link.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2 space-y-3">
          {loading && (
            <div className="text-center text-sm text-zinc-500 py-6">Carregando links...</div>
          )}
          {!loading && approved.length === 0 && (
            <div className="text-center text-sm text-zinc-500 py-6">
              Nenhuma lauda aprovada neste espelho. Aprove ao menos uma lauda para gerar links.
            </div>
          )}
          {!loading && approved.map((r) => {
            const links = (linksByRetranca[r.id] || [])
              .filter((l) => !l.revoked && !isExpired(l));
            return (
              <div key={r.id} data-testid={`tp-share-row-${r.id}`} className="border border-white/10 rounded-sm bg-black/40 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-amber-400 truncate">
                      {r.type} · {r.title || "Sem título"}
                    </div>
                    {r.presenter && (
                      <div className="text-[11px] text-zinc-500 truncate">— {r.presenter}</div>
                    )}
                  </div>
                  {links.length === 0 && (
                    <Button
                      data-testid={`tp-share-create-${r.id}`}
                      onClick={() => createLink(r.id)}
                      disabled={busyId === r.id}
                      size="sm"
                      className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white h-8"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      {busyId === r.id ? "Gerando..." : "Gerar link"}
                    </Button>
                  )}
                </div>
                {links.map((l) => {
                  const url = publicUrlFor(l.token);
                  const isLive = activeSyncToken === l.token;
                  return (
                    <div
                      key={l.id}
                      data-testid={`tp-share-link-${l.id}`}
                      className={`mt-2 rounded-sm border p-2 ${isLive ? "border-amber-400 bg-amber-400/5" : "border-white/10 bg-black/30"}`}
                    >
                      <div className="flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                        <code className="text-[11px] font-mono bg-white/5 text-zinc-200 px-2 py-1 rounded-sm flex-1 truncate">
                          {url}
                        </code>
                        <button
                          data-testid={`tp-share-copy-${l.id}`}
                          onClick={() => { copy(url); toast.success("Copiado!"); }}
                          className="text-[10px] uppercase tracking-wider text-zinc-300 hover:text-white flex items-center gap-1 px-2 py-1 border border-white/10 rounded-sm"
                          title="Copiar"
                        >
                          <Copy className="w-3 h-3" /> Copiar
                        </button>
                        <button
                          data-testid={`tp-share-sync-${l.id}`}
                          onClick={() => toggleSync(l.token)}
                          className={`text-[10px] uppercase tracking-wider flex items-center gap-1 px-2 py-1 rounded-sm border ${isLive ? "bg-amber-400 text-black border-amber-400" : "border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"}`}
                          title={isLive ? "Encerrar sincronização" : "Sincronizar controles com este link"}
                        >
                          {isLive ? <RadioTower className="w-3 h-3" /> : <Radio className="w-3 h-3" />}
                          {isLive ? "Ao vivo" : "Sincronizar"}
                        </button>
                        <button
                          data-testid={`tp-share-revoke-${l.id}`}
                          onClick={() => revoke(l)}
                          className="text-[10px] text-zinc-400 hover:text-red-400 px-2 py-1"
                          title="Revogar"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="mt-1 text-[10px] text-zinc-500">
                        Expira em {l.expires_at ? new Date(l.expires_at).toLocaleDateString("pt-BR") : "—"}
                        {isLive && (
                          <span className="ml-2 text-amber-300 font-bold uppercase">controles espelhados</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
